import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { generateSlots } from "@/lib/utils-uploads";
import { Wrench, Settings2, ShieldCheck, Clock, CheckCircle2 } from "lucide-react";

const CATEGORY_OPTIONS = [
  { id: "phones", label: "Smartphone" },
  { id: "laptops", label: "Laptop / Tablet" },
  { id: "audio", label: "Audio (headphones / speakers)" },
  { id: "wearables", label: "Smartwatch / Wearable" },
  { id: "tv", label: "Television" },
  { id: "gaming", label: "Gaming Console" },
  { id: "accessories", label: "Accessory" },
];

export default function Services() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const slots = useMemo(() => generateSlots(3), []);

  const [catalog, setCatalog] = useState({ devices: {}, issues: { repair: {}, installation: {} } });
  const [serviceType, setServiceType] = useState("repair");
  const [form, setForm] = useState({
    device_category: "phones",
    brand: "",
    model: "",
    issue_type: "",
    notes: "",
    slot_value: "",
  });
  const [addr, setAddr] = useState({
    full_name: "",
    phone: "",
    line1: "",
    city: "",
    pincode: "",
    landmark: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/catalog/services").then(({ data }) => setCatalog(data));
  }, []);

  const brands = Object.keys(catalog.devices?.[form.device_category] || {});
  const models = catalog.devices?.[form.device_category]?.[form.brand] || [];
  const issues = catalog.issues?.[serviceType]?.[form.device_category] || [];

  const setF = (k) => (e) => setForm((s) => {
    const next = { ...s, [k]: e.target.value };
    if (k === "device_category") { next.brand = ""; next.model = ""; next.issue_type = ""; }
    if (k === "brand") next.model = "";
    return next;
  });
  const setA = (k) => (e) => setAddr((s) => ({ ...s, [k]: e.target.value }));

  // Reset issue_type when serviceType changes
  useEffect(() => {
    setForm((s) => ({ ...s, issue_type: "" }));
  }, [serviceType]);

  const priceEstimate = useMemo(() => {
    let base = serviceType === "repair" ? 499 : 299;
    if (["laptops", "tv"].includes(form.device_category)) base += 300;
    if (["phones", "wearables"].includes(form.device_category)) base += 100;
    return base;
  }, [serviceType, form.device_category]);

  const selectedSlot = slots.find((s) => s.value === form.slot_value);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please sign in"); navigate("/login"); return; }
    if (!form.brand || !form.model || !form.issue_type || !form.slot_value ||
        !addr.full_name || !addr.phone || !addr.line1 || !addr.city || !addr.pincode) {
      toast.error("Please fill all required fields"); return;
    }
    setSubmitting(true);
    try {
      await api.post("/services/book", {
        service_type: serviceType,
        device_category: form.device_category,
        brand: form.brand,
        model: form.model,
        issue_type: form.issue_type,
        issue: form.notes || form.issue_type,
        slot: form.slot_value,
        slot_label: selectedSlot?.label || "",
        address: addr,
      });
      toast.success("Service booked! Technician will confirm shortly.");
      navigate("/profile");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not book");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-6 py-8" data-testid="services-page">
      <p className="vm-overline">Services</p>
      <h1 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-2 leading-[1.05]">
        Repair & installation,<br />
        at your <span className="text-volt-500">doorstep</span>.
      </h1>
      <p className="text-gray-600 mt-3 max-w-2xl">
        Just pick from dropdowns — no typing. Certified technicians arrive in your chosen slot.
      </p>

      <div className="grid sm:grid-cols-3 gap-3 mt-6">
        {[
          { icon: Clock, t: "Same-day visits", d: "Slots from morning to night" },
          { icon: ShieldCheck, t: "Certified pros", d: "Background-checked techs" },
          { icon: Wrench, t: "Genuine parts", d: "OEM parts for repairs" },
        ].map((b, i) => (
          <div key={i} className="vm-card p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-volt-500/10 flex items-center justify-center"><b.icon className="h-5 w-5 text-volt-500" /></div>
            <div><p className="font-semibold text-sm text-gray-900">{b.t}</p><p className="text-xs text-gray-500">{b.d}</p></div>
          </div>
        ))}
      </div>

      {/* Service type toggle */}
      <div className="flex gap-2 mt-7">
        {[
          { id: "repair", label: "Repair", icon: Wrench, desc: "Fix a problem" },
          { id: "installation", label: "Installation", icon: Settings2, desc: "Set up new device" },
        ].map((t) => {
          const active = serviceType === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setServiceType(t.id)}
              className={`flex-1 sm:flex-none px-4 py-3 rounded-xl text-left flex items-center gap-3 transition-all border ${
                active
                  ? "bg-volt-500 border-volt-500 text-white shadow-soft"
                  : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
              }`}
              data-testid={`service-type-${t.id}`}
            >
              <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${active ? "bg-white/20" : "bg-volt-500/10"}`}>
                <t.icon className={`h-4 w-4 ${active ? "text-white" : "text-volt-500"}`} />
              </div>
              <div className="leading-tight">
                <p className={`font-semibold text-sm ${active ? "text-white" : "text-gray-900"}`}>{t.label}</p>
                <p className={`text-xs ${active ? "text-white/80" : "text-gray-500"}`}>{t.desc}</p>
              </div>
            </button>
          );
        })}
      </div>

      <form onSubmit={submit} className="grid lg:grid-cols-12 gap-5 mt-6" data-testid="services-form">
        <div className="lg:col-span-8 space-y-5">
          <div className="vm-card p-6">
            <h3 className="font-semibold text-base text-gray-900">Device</h3>
            <div className="grid sm:grid-cols-2 gap-4 mt-3">
              <Field label="Device type">
                <select className="vm-input" value={form.device_category} onChange={setF("device_category")} data-testid="svc-category">
                  {CATEGORY_OPTIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="Brand">
                <select className="vm-input" value={form.brand} onChange={setF("brand")} data-testid="svc-brand">
                  <option value="">Select brand</option>
                  {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
              <Field label="Model" className="sm:col-span-2">
                <select className="vm-input" value={form.model} onChange={setF("model")} disabled={!form.brand} data-testid="svc-model">
                  <option value="">{form.brand ? "Select model" : "Pick a brand first"}</option>
                  {models.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
            </div>
          </div>

          <div className="vm-card p-6">
            <h3 className="font-semibold text-base text-gray-900">
              What do you need {serviceType === "repair" ? "fixed" : "set up"}?
            </h3>
            {issues.length === 0 ? (
              <p className="text-sm text-gray-500 mt-3">
                No {serviceType} options for this device category. Try a different category.
              </p>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5 mt-3">
                {issues.map((it) => {
                  const active = form.issue_type === it;
                  return (
                    <button
                      key={it}
                      type="button"
                      onClick={() => setForm((s) => ({ ...s, issue_type: it }))}
                      className={`text-left p-3 rounded-xl border transition-all ${
                        active
                          ? "border-volt-500 bg-volt-500/5 ring-2 ring-volt-500/20"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                      data-testid={`svc-issue-${it.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-gray-900">{it}</span>
                        {active && <CheckCircle2 className="h-4 w-4 text-volt-500 shrink-0" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            <Field label={`Any details? (optional)`} className="mt-5">
              <textarea
                className="vm-input min-h-[80px]"
                value={form.notes}
                onChange={setF("notes")}
                placeholder="e.g. screen cracked after a drop, charges slowly"
                data-testid="svc-notes"
              />
            </Field>
          </div>

          <div className="vm-card p-6">
            <h3 className="font-semibold text-base text-gray-900">Pick a slot</h3>
            <select className="vm-input mt-3" value={form.slot_value} onChange={setF("slot_value")} data-testid="svc-slot">
              <option value="">Select a slot</option>
              {slots.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          <div className="vm-card p-6">
            <h3 className="font-semibold text-base text-gray-900">Visit address</h3>
            <div className="grid sm:grid-cols-2 gap-4 mt-3">
              <Field label="Full name" className="sm:col-span-2"><input className="vm-input" value={addr.full_name} onChange={setA("full_name")} data-testid="svc-name" /></Field>
              <Field label="Phone"><input className="vm-input" value={addr.phone} onChange={setA("phone")} data-testid="svc-phone" /></Field>
              <Field label="Pincode"><input className="vm-input" value={addr.pincode} onChange={setA("pincode")} data-testid="svc-pincode" /></Field>
              <Field label="Address" className="sm:col-span-2"><input className="vm-input" value={addr.line1} onChange={setA("line1")} placeholder="House no, street, area" data-testid="svc-line1" /></Field>
              <Field label="City"><input className="vm-input" value={addr.city} onChange={setA("city")} data-testid="svc-city" /></Field>
              <Field label="Landmark (optional)"><input className="vm-input" value={addr.landmark} onChange={setA("landmark")} data-testid="svc-landmark" /></Field>
            </div>
          </div>
        </div>

        {/* Summary sidebar */}
        <div className="lg:col-span-4">
          <div className="vm-card p-6 sticky top-24" data-testid="svc-summary">
            <p className="vm-overline">Your booking</p>
            <div className="mt-3 space-y-2">
              <SummaryRow k="Service" v={serviceType === "repair" ? "Repair" : "Installation"} />
              <SummaryRow k="Device" v={form.brand && form.model ? `${form.brand} ${form.model}` : "—"} />
              <SummaryRow k="Issue" v={form.issue_type || "—"} />
              <SummaryRow k="Slot" v={selectedSlot?.label || "—"} />
            </div>

            <div className="vm-divider my-5" />
            <div className="flex items-end justify-between">
              <div>
                <p className="text-xs text-gray-500">Starting price</p>
                <p className="font-display text-3xl font-extrabold text-gray-900">
                  ₹{priceEstimate}
                  <span className="text-sm font-normal text-gray-500"> + parts</span>
                </p>
              </div>
              <span className="vm-chip vm-chip-primary">No visit fee</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">Final price quoted after diagnosis.</p>

            <button
              type="submit"
              disabled={submitting}
              className="vm-btn-primary w-full mt-5"
              data-testid="svc-submit"
            >
              {submitting ? "Booking..." : `Confirm ${serviceType === "repair" ? "repair" : "installation"}`}
            </button>
            <p className="text-xs text-center text-gray-500 mt-2">No payment now — pay after the visit.</p>
          </div>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs font-semibold text-gray-600">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function SummaryRow({ k, v }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-500">{k}</span>
      <span className="font-medium text-gray-900 text-right max-w-[60%] truncate">{v}</span>
    </div>
  );
}
